// External Libraries
import React from "react";
import Modal from "react-responsive-modal";
import * as Icon from "react-feather";
import _ from "lodash";

// Services
import CommunicationService from "../../../services/CommunicationService";
import UtilityService from "../../../services/UtilityService";
import UserService from "../../../services/UserService";

// Common Components
import WComponent from "../../common/WComponent";
import Button from "../../common/button/Button";
import TextFieldInput from "../../common/text_field_input/TextFieldInput";
import DateSelector from "../../common/date_selector/DateSelector";
import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";
import UserAssigner from "../../common/user_assigner/UserAssigner";

// Styles
import "./communications.css";

const { alert } = UtilityService;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Matches "name@domain.com" with no spaces.

const DURATIONS = [
	{ id: 30,  name: "30 minutes" },
	{ id: 45,  name: "45 minutes" },
	{ id: 60,  name: "1 hour" },
	{ id: 90,  name: "1.5 hours" },
	{ id: 120, name: "2 hours" },
];

const initialState = props => ({
	saving: false,
	reviewing: false,
	googleConnected: null,
	googleEmail: "",
	title: "",
	startDate: new Date(),
	durationMinutes: 60,
	selectedDurationName: "1 hour",
	description: "",
	recipients: props && props.clientEmail ? [{ email: props.clientEmail, source: "client" }] : [],
	staffUsers: [],
	manualEmail: "",
	scheduled: null,
	copied: false,
	availability: null,
	checkingAvailability: false,
});

class ScheduleMeetingModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = initialState(props);
		this.contactDropdown = null;

		// Date, duration and team edits come in bursts; one check per burst, and a stale response never wins
		this.checkAvailabilityDebounce = _.debounce(this.checkAvailability, 400);
		this.availabilityRequestId = 0;
	}

	componentWillUnmount() {
		this.checkAvailabilityDebounce.cancel();
		this.availabilityRequestId++;
	}

	componentDidUpdate = prevProps => {
		if (prevProps.show !== this.props.show) {
			// Nothing pending or in flight from the previous open may land on the reset state
			this.checkAvailabilityDebounce.cancel();
			this.availabilityRequestId++;
		}

		if (prevProps.show === false && this.props.show === true) {
			this.update(initialState(this.props));
			this.fetchGoogleStatus();
		}
	};

	fetchGoogleStatus = async () => {
		const status = await CommunicationService.getGoogleStatus();
		await this.update({ googleConnected: !!(status && status.connected), googleEmail: (status && status.email) || "" });
		this.checkAvailabilityDebounce();
	};

	checkAvailability = async () => {
		let { clientId } = this.props;
		let { googleConnected, startDate, durationMinutes, staffUsers } = this.state;

		if (!googleConnected || !startDate) {
			return;
		}

		let requestId = ++this.availabilityRequestId;
		await this.update({ checkingAvailability: true });

		let attendees = await CommunicationService.getMeetingAvailability({
			clientId,
			startAt: startDate.toISOString(),
			durationMinutes: parseInt(durationMinutes),
			attendeeUserIds: staffUsers.map(user => user.user_id),
		});

		if (requestId !== this.availabilityRequestId) {
			return;
		}

		await this.update({ availability: attendees, checkingAvailability: false });
	};

	// Week view of the chosen date, built from local date parts so late-evening slots stay on the right day;
	getCalendarUrl = () => {
		let { startDate, googleEmail } = this.state;
		let date = startDate instanceof Date ? startDate : new Date();
		let url = `https://calendar.google.com/calendar/r/week/${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;

		return googleEmail ? `${url}?authuser=${encodeURIComponent(googleEmail)}` : url;
	};

	onChange = e => {
		let { name, value } = e.target;
		this.update({ [name]: value });
	};

	onDurationSelect = async item => {
		await this.update({ durationMinutes: item.id, selectedDurationName: item.name });
		this.checkAvailabilityDebounce();
	};

	onStartDateSelect = async date => {
		await this.update({ startDate: date });
		this.checkAvailabilityDebounce();
	};

	addRecipient = async ({ email, source }) => {
		let { recipients } = this.state;

		let normalized = String(email || "").trim();

		if (!EMAIL_REGEX.test(normalized)) {
			alert({ type: "error", text: "That doesn't look like a valid email address." });
			return false;
		}

		if (recipients.some(recipient => recipient.email.toLowerCase() === normalized.toLowerCase())) {
			alert({ type: "info", text: "That email is already on the invite list." });
			return false;
		}

		await this.update({ recipients: [...recipients, { email: normalized, source }] });
		return true;
	};

	onRemoveRecipient = async index => {
		let { recipients } = this.state;
		await this.update({ recipients: recipients.filter((recipient, i) => i !== index) });
	};

	onContactSelect = async contact => {
		if (!contact || !contact.email) {
			alert({ type: "info", text: "This contact has no email address on file." });
			return;
		}

		await this.addRecipient({ email: contact.email, source: "contact" });

		if (this.contactDropdown && this.contactDropdown.clear) {
			this.contactDropdown.clear();
		}
	};

	onAddManualEmail = async () => {
		let { manualEmail } = this.state;

		const added = await this.addRecipient({ email: manualEmail, source: "manual" });

		if (added) {
			await this.update({ manualEmail: "" });
		}
	};

	onStaffChange = async ({ selectedUsers }) => {
		await this.update({ staffUsers: selectedUsers.slice() });
		this.checkAvailabilityDebounce();
	};

	onReview = async () => {
		let { title, startDate, googleConnected } = this.state;

		if (!title.trim() || !startDate) {
			alert({ type: "error", text: "Please fill in a title and select a date and time." });
			return;
		}

		if (googleConnected === false) {
			alert({ type: "error", text: "Connect your Google account (Edit Profile) before scheduling meetings." });
			return;
		}

		await this.update({ reviewing: true });
	};

	onBack = async () => {
		await this.update({ reviewing: false });
	};

	onSchedule = async () => {
		let { clientId } = this.props;
		let { title, startDate, durationMinutes, description, recipients, staffUsers } = this.state;

		await this.update({ saving: true });

		const attendeeEmails = recipients.map(recipient => recipient.email);
		const attendeeUserIds = staffUsers.map(user => user.user_id);

		try {
			const result = await CommunicationService.scheduleMeeting({
				clientId,
				title: title.trim(),
				startAt: startDate.toISOString(),
				durationMinutes: parseInt(durationMinutes),
				description: description.trim() || undefined,
				attendeeEmails,
				attendeeUserIds,
			});

			if (result) {
				await this.update({
					scheduled: {
						meetLink: result.meetLink || "",
						attendees: result.attendees || [],
					},
				});
			} else {
				alert({ type: "error", text: "Could not schedule meeting. Make sure your Google account is connected." });
			}
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Could not schedule meeting. Make sure your Google account is connected." });
		}

		await this.update({ saving: false });
	};

	onCopyLink = async () => {
		let { scheduled } = this.state;
		if (!scheduled || !scheduled.meetLink) return;

		const copied = await UtilityService.copyToClipboard({ text: scheduled.meetLink });
		if (!copied) {
			alert({ type: "error", text: "Could not copy the link. Please copy it manually." });
			return;
		}

		await this.update({ copied: true });
		setTimeout(() => this.update({ copied: false }), 2000);
	};

	renderAvailability() {
		let { availability, checkingAvailability, staffUsers } = this.state;

		if (!availability && !checkingAvailability) {
			return null;
		}

		let { userId } = UserService.getUserData();
		let formatTime = value => UtilityService.formatDate({ value, format: "h:mm A" });

		return (
			<div className="schedule-modal__availability">
				<div className="schedule-modal__section-title">Availability</div>

				{checkingAvailability ? (
					<span className="schedule-modal__availability__hint">Checking calendars...</span>
				) : (
					<div className="schedule-modal__availability__chips">
						{availability.map(entry => {
							let staffUser = staffUsers.find(user => user.user_id === entry.userId);
							let name = entry.userId === userId ? "You" : (staffUser && staffUser.userName) || "Team member";
							let text = entry.status === "busy" ? `Busy ${formatTime(entry.busy[0].start)} - ${formatTime(entry.busy[0].end)}` : entry.status === "free" ? "Free" : "Unknown";

							return (
								<div key={entry.userId} className={`schedule-modal__availability__chip schedule-modal__availability__chip--${entry.status}`}>
									<span>{name}</span>
									<span className="schedule-modal__availability__chip__status">{text}</span>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	}

	renderRecipients() {
		let { clientEmail } = this.props;
		let { recipients, staffUsers, manualEmail } = this.state;

		return (
			<>
				<div className="schedule-modal__section-title">Invitees</div>

				{!clientEmail && (
					<div className="schedule-modal__no-email">
						<Icon.AlertTriangle size={13} />
						<span>
							No email is on file for this client. Add an email below to send an invitation, or continue without one — the meeting will still be
							created.
						</span>
					</div>
				)}

				{recipients.length > 0 && (
					<div className="schedule-modal__recipients">
						{recipients.map((recipient, index) => (
							<div key={recipient.email} className="schedule-modal__recipients__chip">
								<span>{recipient.email}</span>
								{recipient.source === "client" && <span className="schedule-modal__recipients__chip__tag">client</span>}
								<Icon.X size={12} className="ft-meet-remove-recipient" onClick={() => this.onRemoveRecipient(index)} />
							</div>
						))}
					</div>
				)}

				<div className="schedule-modal__row">
					<SearchableDropdown
						containerClass="schedule-modal__input"
						ref={ref => (this.contactDropdown = ref)}
						title="Add a contact"
						type={"contacts"}
						clientId={this.props.clientId}
						onSelect={this.onContactSelect}
						showAll
						className="ft-meet-contact"
					/>

					<div className="schedule-modal__manual-email">
						<TextFieldInput
							containerClass="schedule-modal__input"
							title="Add an email"
							name="manualEmail"
							value={manualEmail}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-meet-manual-email"
						/>
						<Button view="secondary" text="Add" onClick={this.onAddManualEmail} disabled={!manualEmail.trim()} />
					</div>
				</div>

				<div className="schedule-modal__section-title">Team Members</div>
				<UserAssigner users={staffUsers} hideRolePicker onChange={this.onStaffChange} containerClass="schedule-modal__user-assigner" />
			</>
		);
	}

	renderReview() {
		let { clientName } = this.props;
		let { saving, title, startDate, selectedDurationName, recipients, staffUsers } = this.state;

		const dateLabel = UtilityService.formatDate({ value: startDate, format: "MMM D, YYYY h:mm A" });
		const inviteCount = recipients.length + staffUsers.length;

		return (
			<>
				<div className="comm-modal__body">
					<div className="schedule-modal__review">
						<div className="schedule-modal__review__row">
							<span className="schedule-modal__review__label">Meeting</span>
							<span>{title}</span>
						</div>
						<div className="schedule-modal__review__row">
							<span className="schedule-modal__review__label">When</span>
							<span>
								{dateLabel} ({selectedDurationName})
							</span>
						</div>
						{clientName && (
							<div className="schedule-modal__review__row">
								<span className="schedule-modal__review__label">Client</span>
								<span>{clientName}</span>
							</div>
						)}

						{inviteCount > 0 ? (
							<>
								<div className="schedule-modal__review__invite-title">A Google Calendar invite with the Meet link will be emailed to:</div>
								<div className="schedule-modal__review__invites">
									{recipients.map(recipient => (
										<div key={recipient.email} className="schedule-modal__review__invites__item">
											<Icon.Mail size={13} />
											<span>{recipient.email}</span>
										</div>
									))}
									{staffUsers.map(user => (
										<div key={user.user_id} className="schedule-modal__review__invites__item">
											<Icon.User size={13} />
											<span>{user.userName} (team)</span>
										</div>
									))}
								</div>
							</>
						) : (
							<div className="schedule-modal__review__warning">
								<Icon.AlertTriangle size={14} />
								<span>No invitations will be sent. The meeting and Meet link will only be created on your calendar — you can share the link manually.</span>
							</div>
						)}
					</div>
				</div>

				<div className="comm-modal__footer">
					<Button view="secondary" text="Back" onClick={this.onBack} disabled={saving} />
					<Button
						text={saving ? "Scheduling..." : inviteCount > 0 ? "Confirm & Send Invites" : "Schedule Without Invites"}
						onClick={this.onSchedule}
						disabled={saving}
						className="ft-meet-confirm"
					/>
				</div>
			</>
		);
	}

	renderSuccess() {
		let { onClose } = this.props;
		let { scheduled, copied } = this.state;

		return (
			<>
				<div className="comm-modal__body">
					<div className="schedule-modal__success">
						<div className="schedule-modal__success__icon">
							<Icon.CheckCircle size={48} strokeWidth={1.5} color="#1e8e3e" />
						</div>
						<div className="schedule-modal__success__title">Meeting scheduled</div>
						<div className="schedule-modal__success__sub">
							{scheduled.attendees.length > 0
								? `A calendar invite has been emailed to: ${scheduled.attendees.join(", ")}`
								: "No invitations were sent — share the Meet link below manually."}
						</div>

						{scheduled.meetLink ? (
							<div className="schedule-modal__link-box">
								<div className="schedule-modal__link-box__url">{scheduled.meetLink}</div>
								<button
									className={`schedule-modal__link-box__copy${copied ? " schedule-modal__link-box__copy--done" : ""}`}
									onClick={this.onCopyLink}
								>
									{copied ? <Icon.Check size={14} /> : <Icon.Copy size={14} />}
									{copied ? "Copied" : "Copy link"}
								</button>
							</div>
						) : (
							<div className="schedule-modal__success__sub">
								No Google Meet link was returned for this event.
							</div>
						)}
					</div>
				</div>

				<div className="comm-modal__footer">
					<a
						className="schedule-modal__open-link"
						href={scheduled.meetLink}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Icon.ExternalLink size={14} />
						Open in Google Meet
					</a>
					<Button text="Done" onClick={onClose} />
				</div>
			</>
		);
	}

	renderForm() {
		let { onClose } = this.props;
		let { saving, title, startDate, selectedDurationName, description, googleConnected } = this.state;

		return (
			<>
				<div className="comm-modal__body">
					{googleConnected === false && (
						<div className="schedule-modal__no-email">
							<Icon.AlertTriangle size={13} />
							<span>Your Google account is not connected. Connect it from User Preferences to schedule meetings.</span>
						</div>
					)}

					<TextFieldInput
						containerClass="schedule-modal__input"
						title="Meeting Title"
						name="title"
						value={title}
						onChange={this.onChange}
						autoComplete="off"
					/>

					<div className="schedule-modal__row">
						<DateSelector
							containerClass="schedule-modal__input"
							title="Date & Time"
							selectedDate={startDate}
							onDateSelect={this.onStartDateSelect}
							showTimeSelect
							dateFormat="MMM d, yyyy h:mm aa"
							timeIntervals={15}
						/>

						<SearchableDropdown
							containerClass="schedule-modal__input"
							title="Duration"
							options={DURATIONS}
							selectedName={selectedDurationName}
							onSelect={this.onDurationSelect}
							showAll
						/>
					</div>

					{this.renderAvailability()}

					<TextFieldInput
						containerClass="schedule-modal__input"
						title="Description (optional)"
						name="description"
						value={description}
						onChange={this.onChange}
						autoComplete="off"
					/>

					{this.renderRecipients()}

					<div className="schedule-modal__note">
						<Icon.Info size={13} />
						<span>This meeting and its transcript will be logged under this client only.</span>
					</div>
				</div>

				<div className="comm-modal__footer">
					<a
						className="schedule-modal__open-link ft-meet-calendar"
						href={this.getCalendarUrl()}
						target="_blank"
						rel="noopener noreferrer"
						title="Check availability in Google Calendar"
					>
						<Icon.Calendar size={14} />
						Open Google Calendar
					</a>
					<Button view="secondary" text="Cancel" onClick={onClose} disabled={saving} />
					<Button text="Review & Schedule" onClick={this.onReview} disabled={saving || googleConnected === false} className="ft-meet-review" />
				</div>
			</>
		);
	}

	render() {
		let { show, onClose, clientName } = this.props;
		let { reviewing, scheduled } = this.state;

		return (
			<Modal open={show} onClose={onClose} classNames={{ modal: "comm-modal" }} center>
				<div className="comm-modal__header">
					<Icon.Video size={22} style={{ flexShrink: 0 }} />
					<div className="comm-modal__header__titles">
						<span className="comm-modal__header__title">Schedule Meeting</span>
						{clientName && (
							<span className="comm-modal__header__sub">with {clientName}</span>
						)}
					</div>
				</div>

				{scheduled ? this.renderSuccess() : reviewing ? this.renderReview() : this.renderForm()}
			</Modal>
		);
	}
}

export default ScheduleMeetingModal;
